import React, { createContext, useContext, useState, useCallback, useMemo } from 'react'
import { flatDictionaries } from '../i18n/dictionaries'

// Basic plural category resolver for Russian / Kyrgyz (similar Slavic) and English.
// Categories: one, few, many, other
const getPluralCategory = (locale, n) => {
  const absN = Math.abs(n)
  switch (locale) {
    case 'ru':
    case 'ky': {
      const mod10 = absN % 10
      const mod100 = absN % 100
      if (mod10 === 1 && mod100 !== 11) return 'one'
      if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'few'
      if (mod10 === 0 || (mod10 >= 5 && mod10 <= 9) || (mod100 >= 11 && mod100 <= 14)) return 'many'
      return 'other'
    }
    case 'en':
    default:
      return n === 1 ? 'one' : 'other'
  }
}

const I18nContext = createContext()

const SUPPORTED = ['ru', 'en', 'ky']
const FALLBACK = 'en'

export const I18nProvider = ({ children, defaultLocale = 'ru' }) => {
  const initial = SUPPORTED.includes(defaultLocale) ? defaultLocale : 'ru'
  const [locale, setLocale] = useState(initial)

  const t = useCallback((key) => {
    const dict = flatDictionaries[locale] || {}
    if (dict[key]) return dict[key]
    // fallback chain
    const fb = flatDictionaries[FALLBACK] || {}
    return fb[key] || key
  }, [locale])

  // plural usage: plural(baseKey, count) expects keys like:
  // my.key.one / my.key.few / my.key.many / my.key.other (fallback to .other)
  const plural = useCallback((baseKey, count) => {
    const category = getPluralCategory(locale, count)
    const attempt = `${baseKey}.${category}`
    const direct = t(attempt)
    if (direct !== attempt) return direct.replace(/{{count}}/g, String(count))
    const fallback = t(`${baseKey}.other`)
    return fallback.replace(/{{count}}/g, String(count))
  }, [locale, t])

  const value = useMemo(() => ({ locale, setLocale, t, plural, locales: SUPPORTED }), [locale, t, plural])
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

export const useI18n = () => {
  const ctx = useContext(I18nContext)
  if (!ctx) throw new Error('useI18n must be used within I18nProvider')
  return ctx
}

export default I18nContext
